import Sentry from "@similarweb/sentry-logger";

import categoryService, { ALL } from "app/common/services/categoryService";
import type { ICategory, IFlattenedCategory } from "app/common/services/categoryService.types";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import * as utils from "app/components/filters-bar/utils";
import { ESharingState } from "app/components/sharingAssetModal/types";
import { getSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";
import {
    MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
    MARKET_RESEARCH_WIDGETS_CONFIG_ID,
} from "app/pages/industry-analysis/config/const";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { RecentService } from "app/services/recent/recentService";
import { getCustomSegments, hasCustomSegments } from "app/services/segments/LinesOfBusinessUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { EMPTY_CUSTOM_SEGMENTS, EMPTY_SEGMENT_MODULE } from "app/services/segments/segmentsApiService";
import { SwRedux } from "app/services/SwRedux";

import { UserCustomCategoryService } from "./category/userCustomCategoryService";

interface IuserCustomCategoriesObj {
    [index: string]: ICategory;
}

export type CategoriesMap = Record<string, IFlattenedCategory>;

interface ICategoryServiceUtils {
    /** get the most queried country for a certain industry, based on recent searches */
    getSuggestedCountry: (industry: ICategory) => ICountryObject;
    getIndustryCountry: (industry: ICategory) => ICountryObject;
    userCustomCategoriesObj: IuserCustomCategoriesObj;
    /** returns true if the industry contains segments */
    hasSegments: (industry: ICategory) => boolean;
    resolveConfigID: (componentConfigId: string, industry: ICategory, industryWithSegmentConfigId?: string) => string;
    isEmptyIndustry: (industry: ICategory) => boolean;
    /** returns true if one of the segments' domains is also in the industry's domain array*/
    isSegmentAndDomainsOverlapped: (industry: ICategory, segmentsModule: ISegmentsModule) => boolean;
    hasUnauthorizedSegment: (industry: ICategory, segmentsModule: ISegmentsModule) => boolean;
    /** Sharing option will be disabled if the custom industry contains segments [at least one] that are not shared with the entire organization */
    resolveDisabledSharingStates: (industry: ICategory) => Array<ESharingState>;
    getCategoriesMap: (attributeKey?: keyof ICategory) => CategoriesMap;
    isMarketResearchWidgetsAvailable(): boolean;
    isAll(): boolean;
    selectedCategoryHasSegments: () => boolean;
}

const getSuggestedCountry = (industry: ICategory) => {
    if (!industry) {
        return utils.getCountries()[0];
    }
    const recentSearches = RecentService.getRecents();
    const filteredRecentSearches = recentSearches.filter(({ data }) => data.category === industry.categoryId);
    const countriesAmountDictionary = filteredRecentSearches.reduce((result, recentItem) => {
        const { data } = recentItem;
        const { country } = data;
        if (!country) {
            return result;
        }
        if (result.hasOwnProperty(country)) {
            return { ...result, [country]: result[country] + 1 };
        }
        return { ...result, [country]: 1 };
    }, {});
    const countries = Object.keys(countriesAmountDictionary);
    let mostVisitedCountry = countries[0];
    countries.forEach((country) => {
        if (countriesAmountDictionary[country] > countriesAmountDictionary[mostVisitedCountry]) {
            mostVisitedCountry = country;
        }
    });
    if (mostVisitedCountry) {
        return CountryService.getCountryById(mostVisitedCountry);
    }
    return utils.getCountries()[0];
};

const getIndustryCountry = (industry: ICategory) => {
    if (!industry) {
        return getSuggestedCountry(null);
    }
    const { country: countryID } = industry;
    if (countryID) {
        const allowedCountry = swSettings.allowedCountry(countryID);
        if (allowedCountry) {
            const country = CountryService.getCountryById(countryID);
            return country;
        }
    }
    const country = getSuggestedCountry(industry);
    return country;
};

const userCustomCategoriesObj = UserCustomCategoryService.getCustomCategories().reduce(
    (prevObj, value) => ({ ...prevObj, [value?.categoryId]: value }),
    {},
);

const hasSegments = (industry: ICategory) => {
    return industry?.segments?.length > 0;
};

const resolveConfigID = (
    componentConfigId: string,
    industry: ICategory,
    industryWithSegmentConfigId = MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
) => {
    return hasSegments(industry) ? industryWithSegmentConfigId : componentConfigId;
};

const resolveDisabledSharingStates = (industry: ICategory) => {
    const disabledSharingStates = [];
    if (!industry || !hasCustomSegments(industry?.segments)) {
        return disabledSharingStates;
    }
    const swRedux = SwRedux.getInstance();
    const { segmentsModule = {} } = swRedux.getState();
    const { customSegmentsMeta = {} } = segmentsModule;
    const { Segments: userSegments = [] } = customSegmentsMeta;
    const customSegments = getCustomSegments(industry.segments);

    customSegments.forEach((segmentID) => {
        const segmentMetaData = userSegments.find(({ id }) => id === segmentID) ?? {};
        const { sharedWithAccounts = [] } = segmentMetaData;
        if (sharedWithAccounts.length === 0) {
            disabledSharingStates.push(ESharingState.ACCOUNT);
            disabledSharingStates.push(ESharingState.SUBSET_OF_USERS);
        }
    });

    return disabledSharingStates;
};

const isEmptyIndustry = (industry: ICategory) => {
    if (!industry) {
        return false;
    }
    const { segments = [], domains = [], isCustomCategory } = industry;
    return segments.length === 0 && domains.length === 0 && isCustomCategory;
};

const isSegmentAndDomainsOverlapped = (industry: ICategory, segmentsModule: ISegmentsModule = EMPTY_SEGMENT_MODULE) => {
    if (!industry) {
        return false;
    }
    const { segments = [], domains = [], isCustomCategory } = industry;
    const customSegments = getCustomSegments(segments);
    if (!isCustomCategory || customSegments.length === 0 || domains.length === 0) {
        return false;
    }
    const { customSegmentsMeta = EMPTY_CUSTOM_SEGMENTS } = segmentsModule;
    const { Segments: userSegments = [] } = customSegmentsMeta;
    const isOverlapped = customSegments.some((segmentID) => {
        const segmentMetaData = userSegments.find(({ id }) => id === segmentID);
        if (!segmentMetaData) {
            return false;
        }
        const { domain } = segmentMetaData;
        return domains.includes(domain);
    });
    return isOverlapped;
};

const hasUnauthorizedSegment = (industry: ICategory, segmentsModule: ISegmentsModule): boolean => {
    if (!industry) {
        return false;
    }
    const { segments = [] } = industry;
    const customSegments = getCustomSegments(segments);
    const { customSegmentsMeta = EMPTY_CUSTOM_SEGMENTS } = segmentsModule;
    const { Segments: userSegments = [], AccountSegments: accountSegments = [] } = customSegmentsMeta;
    const allSegmentsIDs = new Set([...userSegments, ...accountSegments].map((segment) => segment.id));
    const result = customSegments.some((segmentID) => !allSegmentsIDs.has(segmentID));
    return result;
};

const getCategoriesMap = (attributeKey = "forApi") => {
    const categories = categoryService.getFlattenedCategoriesList();
    const categoriesMap = categories.reduce((map, category) => {
        return { ...map, [category[attributeKey]]: category };
    }, {});
    return categoriesMap;
};

const isMarketResearchWidgetsAvailable = () => {
    return isAvailable(swSettings.components[MARKET_RESEARCH_WIDGETS_CONFIG_ID]);
};

const isAll = () => {
    const category = getSelectedCategory();
    return category.id === ALL;
};

const selectedCategoryHasSegments = () => {
    const selectedCategory = getSelectedCategory();
    if (!selectedCategory) {
        const { stack } = new Error();
        Sentry.warning(`Selected category is not found. stack=${stack}`);
        return false;
    }
    return hasSegments(selectedCategory);
};
export const CategoryServiceUtils: ICategoryServiceUtils = {
    getSuggestedCountry,
    getIndustryCountry,
    userCustomCategoriesObj,
    hasSegments,
    resolveConfigID,
    resolveDisabledSharingStates,
    isEmptyIndustry,
    isSegmentAndDomainsOverlapped,
    hasUnauthorizedSegment,
    getCategoriesMap,
    isMarketResearchWidgetsAvailable,
    isAll,
    selectedCategoryHasSegments,
};
